import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Analytics01Icon,
  BankIcon,
  Invoice03Icon,
  MoneyReceive02Icon,
  MoneySend02Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";
import useSpendingData from "hooks/useSpendingData";
import { formatCashFull } from "utils/formatCash";
import type { QueueSummary } from "hooks/useDashboardQueues";
import type { ProgramInsight } from "components/common/ProgramInsightsPanel";

/** Which surface an insight came from, which sets its icon and its route. */
type InsightKind =
  | "invoice"
  | "payment"
  | "balance"
  | "reimbursement"
  | "category";

type RankedInsight = Omit<ProgramInsight, "icon" | "wrapper" | "route"> & {
  kind: InsightKind;
  severity: "critical" | "warning";
};

/** Each kind's icon and tint, and the surface that can actually clear it. */
const DISPLAY_BY_KIND: Record<
  InsightKind,
  Pick<ProgramInsight, "icon" | "wrapper" | "route">
> = {
  invoice: { icon: Invoice03Icon, wrapper: "danger", route: "/admin/invoices" },
  payment: {
    icon: MoneySend02Icon,
    wrapper: "warning",
    route: "/admin/payments",
  },
  balance: { icon: BankIcon, wrapper: "warning", route: "/admin/transactions" },
  reimbursement: {
    icon: MoneyReceive02Icon,
    wrapper: "warning",
    route: "/admin/reimbursements",
  },
  category: {
    icon: Analytics01Icon,
    wrapper: "warning",
    route: "/admin/analytics",
  },
};

export interface ProgramInsightsInput {
  payments: QueueSummary;
  invoices: QueueSummary;
  reimbursements: QueueSummary;
  /** Cash across the company's own accounts. */
  balance: number;
  /** Money out over the last 30 days, which sets the runway. */
  outflow: number;
  currency: string;
}

/** Below this many days of runway the balance is worth interrupting someone for. */
const RUNWAY_CRITICAL_DAYS = 21;

/** A category has to move this much before it is news rather than noise. */
const CATEGORY_JUMP_PERCENT = 15;

/** Past this, an overdue receivable stops being a chase and becomes a risk. */
const INVOICE_CRITICAL_DAYS = 30;

const DAYS_IN_WINDOW = 30;

/**
 * The things on this dashboard worth acting on, ranked worst first.
 *
 * Every insight is derived from data the page already holds, apart from the
 * category comparison, which is the one question none of the tiles answer. The
 * thresholds live here rather than in the panel: they are the judgement, and
 * the panel should not be able to disagree with the ranking it renders.
 */
const useProgramInsights = ({
  payments,
  invoices,
  reimbursements,
  balance,
  outflow,
  currency,
}: ProgramInsightsInput): { insights: ProgramInsight[]; loading: boolean } => {
  const { t } = useTranslation();

  const { pieData: thisMonth, loading: thisMonthLoading } = useSpendingData({
    type: "pie",
    groupKey: "expenseCategory",
    filters: { timeFrame: TimeFrameEnum.THIS_MONTH },
  });

  const { pieData: lastMonth, loading: lastMonthLoading } = useSpendingData({
    type: "pie",
    groupKey: "expenseCategory",
    filters: { timeFrame: TimeFrameEnum.LAST_MONTH },
  });

  const insights = useMemo(() => {
    const found: RankedInsight[] = [];
    const money = (amount: number) => formatCashFull(amount, currency);

    const overdueInvoice = invoices.oldest;
    if (overdueInvoice) {
      found.push({
        id: "invoice-overdue",
        kind: "invoice",
        severity:
          overdueInvoice.days >= INVOICE_CRITICAL_DAYS ? "critical" : "warning",
        title: t("Invoice {{days}} days overdue", {
          days: overdueInvoice.days,
        }),
        details: [overdueInvoice.name, money(overdueInvoice.amount)].filter(
          Boolean,
        ),
      });
    }

    const overduePayment = payments.oldest;
    if (overduePayment) {
      found.push({
        id: "payment-overdue",
        kind: "payment",
        severity: "warning",
        title: t("Bill {{days}} days past due", { days: overduePayment.days }),
        details: [overduePayment.name, money(overduePayment.amount)].filter(
          Boolean,
        ),
      });
    }

    // Runway only means anything once money has actually left: a company with
    // no outflow in the window has no burn rate to divide by.
    const dailyBurn = outflow / DAYS_IN_WINDOW;
    const runway = dailyBurn > 0 ? Math.floor(balance / dailyBurn) : null;
    if (runway !== null && runway <= RUNWAY_CRITICAL_DAYS) {
      found.push({
        id: "balance-runway",
        kind: "balance",
        severity: "critical",
        title: t("Low operating balance"),
        details: [money(balance), t("~{{days}} days runway", { days: runway })],
      });
    }

    const agingReimbursement = reimbursements.oldest;
    if (agingReimbursement && reimbursements.overdue > 0) {
      found.push({
        id: "reimbursement-aging",
        kind: "reimbursement",
        severity: "warning",
        title: t("Reimbursement aging {{days}} days", {
          days: agingReimbursement.days,
        }),
        details: [
          agingReimbursement.name,
          money(agingReimbursement.amount),
        ].filter(Boolean),
      });
    }

    const previousByName = new Map(
      lastMonth.map((entry) => [entry.name, entry.value]),
    );
    const risers = thisMonth
      .map((entry) => {
        const before = previousByName.get(entry.name) ?? 0;
        if (before <= 0) return null;
        const change = Math.round(((entry.value - before) / before) * 100);
        return change >= CATEGORY_JUMP_PERCENT
          ? { name: entry.name, change }
          : null;
      })
      .filter((riser): riser is { name: string; change: number } => !!riser)
      .sort((a, b) => b.change - a.change);

    if (risers.length > 0) {
      const [top] = risers;
      found.push({
        id: "category-jump",
        kind: "category",
        severity: "warning",
        // The category name is stored data, so it is interpolated rather than
        // looked up: a bare t() on it would eat everything before a colon.
        title: t("{{category}} spend up {{percent}}%", {
          category: top.name,
          percent: top.change,
        }),
        details: [t("vs last month")],
      });
    }

    const rank = { critical: 0, warning: 1 };
    return found
      .sort((a, b) => rank[a.severity] - rank[b.severity])
      .map(({ kind, ...insight }) => ({
        ...insight,
        ...DISPLAY_BY_KIND[kind],
      }));
  }, [
    t,
    currency,
    invoices.oldest,
    payments.oldest,
    reimbursements.oldest,
    reimbursements.overdue,
    balance,
    outflow,
    thisMonth,
    lastMonth,
  ]);

  return {
    insights,
    loading:
      thisMonthLoading ||
      lastMonthLoading ||
      invoices.loading ||
      payments.loading ||
      reimbursements.loading,
  };
};

export default useProgramInsights;
